import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { fiscal, ApiError } from "../lib/api";
import { money } from "../lib/utils";

// Fiscal: bandeja de documentos (from('fiscal_documents') con RLS) con estado,
// reintento de emisión (pac-emit) y reemisión de rechazados (fiscal_reissue).
export const Route = createFileRoute("/fiscal")({ component: Fiscal });

interface Doc {
  id: string; doc_type: string; doc_number: number | null; cufe: string; status: string;
  total: number; receiver_name: string | null; created_at: string; sale_id: string | null;
}

const STATUS_STYLE: Record<string, string> = {
  authorized: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  queued: "bg-amber-100 text-amber-700",
  signed_contingency: "bg-offline/20 text-offline",
  transmitted: "bg-blue-100 text-blue-700",
};

function Fiscal() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["fiscal_docs"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fiscal_documents")
        .select("id, doc_type, doc_number, cufe, status, total, receiver_name, created_at, sale_id")
        .order("created_at", { ascending: false }).limit(100);
      if (error) throw error;
      return (data ?? []) as Doc[];
    },
  });

  const retry = useMutation({
    mutationFn: (d: Doc) => fiscal.emit(d.sale_id!, d.doc_type),
    onSuccess: () => { setMsg("Reintento enviado al PAC."); void qc.invalidateQueries({ queryKey: ["fiscal_docs"] }); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold">Documentos fiscales</h1>
      {msg && <div className="mb-4 rounded-lg bg-grafito-100 px-4 py-2 text-sm">{msg}</div>}
      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-grafito-50 text-left text-grafito-400">
            <tr><th className="px-4 py-2">Tipo</th><th className="px-4 py-2">N°</th><th className="px-4 py-2">Receptor</th><th className="px-4 py-2 text-right">Total</th><th className="px-4 py-2">Estado</th><th className="px-4 py-2" /></tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={6} className="px-4 py-8 text-center text-grafito-400">Cargando…</td></tr>
              : (data ?? []).map((d) => (
                <tr key={d.id} className="border-t">
                  <td className="px-4 py-2 text-xs">{d.doc_type}</td>
                  <td className="px-4 py-2 font-mono text-xs">{d.doc_number ?? "—"}</td>
                  <td className="px-4 py-2">{d.receiver_name ?? "Consumidor Final"}</td>
                  <td className="px-4 py-2 text-right">{money(d.total)}</td>
                  <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[d.status] ?? "bg-grafito-100"}`}>{d.status}</span></td>
                  <td className="px-4 py-2 text-right">
                    {(d.status === "queued" || d.status === "signed_contingency" || d.status === "rejected") && d.sale_id && (
                      <button onClick={() => retry.mutate(d)} disabled={retry.isPending} className="btn btn-ghost py-1 text-xs">
                        {d.status === "rejected" ? "Reemitir" : "Reintentar"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-grafito-400">
        Los documentos en cola/contingencia los reintenta también el cron <code>fiscal-reconcile</code> automáticamente; a las 72h sin autorizar levanta alerta.
      </p>
    </div>
  );
}
